// Who this device signed in as. A workspace member (a grant with workspace
// scopes, accepted on the token screen) gets the real app narrowed to the
// workspaces shared with them: the server sends and accepts only those
// (internal/member), and the app hides what would only fail for them. The flag
// is decided by the server at sign-in (`/api/config` says `member`) and kept in
// meta so a cold start has it before the first paint.
import { create } from 'zustand'
import { SETTINGS_ID } from '@/kinds/settings/schema'
import { META } from '@/db/schema'

/** The kinds a member's writes may reach; anything else stays on the device. */
export const MEMBER_KINDS = ['Task', 'Project', 'Page', 'List', 'ThemeProfile'] as const

export interface Access {
  member: boolean
  grant: string | null
  /** a member's own `from:` handle: a label naming her is hers, not news to her */
  from: string | null
  /** an admin member (2026-10-17): the owner's app minus what is his alone; the server decides, this only shapes the app */
  admin: boolean
  /** what the member is called ("Keira"), set by the owner on her grant */
  name: string | null
  /** an admin's own Settings record (look and layout), made from her grant by the server */
  settingsId: string | null
}

export const useAccess = create<Access>(() => ({
  member: false,
  grant: null,
  from: null,
  admin: false,
  name: null,
  settingsId: null,
}))

/** What `/api/config` says about who this token is. */
export interface AccessConfig {
  member?: boolean
  member_grant?: string
  member_from?: string
  member_name?: string
  member_admin?: boolean
  member_settings?: string
}

export const accessOf = (cfg: AccessConfig): Access => ({
  member: !!cfg.member,
  grant: cfg.member ? (cfg.member_grant ?? null) : null,
  from: cfg.member ? (cfg.member_from ?? null) : null,
  admin: !!cfg.member && !!cfg.member_admin && !!cfg.member_settings,
  name: cfg.member ? (cfg.member_name ?? null) : null,
  settingsId: cfg.member && cfg.member_admin ? (cfg.member_settings ?? null) : null,
})

/** The id of the Settings record this device reads and writes: the owner's, or an admin member's own. */
export const settingsIdNow = (): string => {
  const a = useAccess.getState()
  return a.admin && a.settingsId ? a.settingsId : SETTINGS_ID
}

/** True when the signed-in device is an admin member. */
export const isAdmin = () => useAccess.getState().admin

/** True when a `from:` label names the signed-in member herself. */
export const isOwnFrom = (
  from: string | undefined,
  access: { member: boolean; from: string | null },
): boolean => access.member && !!from && !!access.from && from.toLowerCase() === access.from.toLowerCase()

/** Outside React: a member's own grant id (null for the owner). */
export const memberGrant = () => useAccess.getState().grant

/** Outside React: whether the signed-in device is a workspace member. */
export const isMember = () => useAccess.getState().member

/** True for ops a member's device may send to the server: edges, and writes to the shared kinds. */
export const memberMaySend = (op: {
  type: string
  kind?: string
  id?: string
  resource?: { kind: string; id?: string }
}): boolean => {
  if (op.type === 'edge') return true
  const kind = op.type === 'create' ? op.resource?.kind : op.kind
  if (!kind) return false
  if ((MEMBER_KINDS as readonly string[]).includes(kind)) return true
  const a = useAccess.getState()
  if (!a.admin) return false
  if (kind === 'Date' || kind === 'Person') return true
  // an admin's Settings are her own record, never the owner's
  return kind === 'Settings' && (op.type === 'create' ? op.resource?.id : op.id) === a.settingsId
}

interface MetaStore {
  getMeta<T>(key: string): Promise<T | undefined>
  setMeta(key: string, value: unknown): Promise<unknown>
}

/** Keep who this token is in meta (a cold start has it before the first paint) and in the store. */
export const rememberAccess = async (db: MetaStore, a: Access) => {
  await db.setMeta(META.member, a.member)
  await db.setMeta(META.memberGrant, a.grant)
  await db.setMeta(META.memberFrom, a.from)
  await db.setMeta(META.memberName, a.name)
  await db.setMeta(META.memberAdmin, a.admin)
  await db.setMeta(META.memberSettings, a.settingsId)
  useAccess.setState(a)
}

/** What meta kept, for the first render. */
export const recalledAccess = async (db: MetaStore): Promise<Access> => {
  const [member, grant, from, name, admin, settingsId] = await Promise.all([
    db.getMeta<boolean>(META.member),
    db.getMeta<string>(META.memberGrant),
    db.getMeta<string>(META.memberFrom),
    db.getMeta<string>(META.memberName),
    db.getMeta<boolean>(META.memberAdmin),
    db.getMeta<string>(META.memberSettings),
  ])
  return {
    member: !!member,
    grant: grant ?? null,
    from: from ?? null,
    name: name ?? null,
    admin: !!member && !!admin && !!settingsId,
    settingsId: settingsId ?? null,
  }
}
